import { useState } from 'react';
import { Button } from '../components/Button';
import { Icon } from '../components/Icon';
import { Modal } from '../components/Modal';
import { useToast } from '../components/Toast';
import { PlantProgress } from '../components/PlantProgress';
import type { PuzzleRef } from '../engine/links';
import type { AnyPuzzleMode, PlayMode, ResultSummary, ScoreBreakdown as Breakdown } from '../engine/types';
import { useToday, useUntilNextDaily } from '../hooks/useToday';
import type { ActiveSession, Challenge } from '../state/GameContext';
import { useProfile } from '../state/ProfileContext';
import { overall, type DailyResult } from '../state/statsLogic';
import { useStats } from '../state/StatsContext';
import { commas, formatClock } from '../utils/format';
import { canNativeShare, copyText, nativeShare } from '../utils/share';
import { renderShareCard, shareImageOrDownload } from '../utils/shareImage';
import { buildShareText, challengeUrl, siteDomain, siteUrl } from '../utils/shareText';
import { ScoreBreakdown } from './ScoreBreakdown';

/** Everything the result card shows — from a just-finished session or a saved daily. */
export interface ResultView {
  mode: AnyPuzzleMode;
  playMode: PlayMode;
  day?: number;
  archive: boolean;
  difficultyLabel: string;
  summary: ResultSummary;
  breakdown: Breakdown;
  eased?: boolean;
  /** The level the player chose, when play was eased below it. */
  requestedLabel?: string;
  challenge?: Challenge | null;
  /** How to reopen this exact puzzle (for the challenge link). */
  ref: PuzzleRef | null;
}

export function viewFromSession(s: ActiveSession): ResultView | null {
  if (!s.result) return null;
  const requested = s.mode.difficulties.find((d) => d.id === s.requestedDifficultyId);
  return {
    mode: s.mode,
    playMode: s.playMode,
    day: s.seed.dayNumber,
    archive: s.archive,
    difficultyLabel: s.difficulty.label,
    summary: s.result.summary,
    breakdown: s.result.breakdown,
    eased: s.eased,
    requestedLabel: requested?.label,
    challenge: s.challenge,
    ref:
      s.playMode === 'daily' && s.seed.dayNumber
        ? { modeId: s.mode.id, kind: 'daily', day: s.seed.dayNumber }
        : s.salt
          ? { modeId: s.mode.id, kind: 'endless', difficultyId: s.difficulty.id, salt: s.salt }
          : null,
  };
}

export function viewFromDaily(mode: AnyPuzzleMode, day: number, archive: boolean, r: DailyResult): ResultView {
  return {
    mode,
    playMode: 'daily',
    day,
    archive,
    difficultyLabel: mode.difficulties.find((d) => d.id === r.difficultyId)?.label ?? '',
    summary: r.summary,
    breakdown: r.breakdown,
    ref: { modeId: mode.id, kind: 'daily', day },
  };
}

export function tagText(v: Pick<ResultView, 'playMode' | 'day' | 'archive'>): string {
  if (v.playMode !== 'daily' || !v.day) return 'Endless';
  return v.archive ? `Daily #${v.day} · archive` : `Daily #${v.day}`;
}

interface ResultModalProps {
  view: ResultView;
  /** Dismiss the card (the finished board stays visible behind it). */
  onClose: () => void;
  onHome: () => void;
  /** Endless: deal another puzzle at the same difficulty. */
  onPlayAgain?: () => void;
  /** Daily: jump to the next unplayed daily. */
  upNext?: { mode: AnyPuzzleMode; remaining: number } | null;
  onUpNext?: () => void;
}

function NextDaily() {
  const ms = useUntilNextDaily();
  return (
    <p className="cv-result__next" role="timer" aria-live="off">
      <Icon name="leaf" size={14} /> New dailies in <span className="cv-mono">{formatClock(ms)}</span>
    </p>
  );
}

export function ResultModal({ view, onClose, onHome, onPlayAgain, upNext, onUpNext }: ResultModalProps) {
  const toast = useToast();
  const { profile } = useProfile();
  const { stats } = useStats();
  const today = useToday();
  const [busy, setBusy] = useState(false);

  const { summary, breakdown } = view;
  const won = summary.status === 'won';
  const reveal = summary.detail ?? summary.answer;
  const revealLabel = summary.revealLabel ?? (summary.detail ? 'Letters' : 'The word was');
  const grid = summary.shareGrid ?? [];
  const tag = tagText(view);
  const link = view.ref ? challengeUrl(view.ref, won ? breakdown.total : undefined, profile?.name) : siteUrl();
  const shareTag = view.playMode === 'daily' && view.day ? `#${view.day}${view.archive ? ' (archive)' : ''}` : '';

  const shareText = () =>
    buildShareText({
      modeName: view.mode.name,
      tag: shareTag,
      difficultyLabel: view.playMode === 'endless' ? view.difficultyLabel : undefined,
      grid,
      headline: summary.headline,
      score: breakdown.total,
      url: link,
    });

  const onShareImage = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const blob = await renderShareCard({
        mode: view.mode.name,
        tag,
        headline: summary.headline,
        grid,
        score: commas(breakdown.total),
        url: siteDomain(),
        won,
      });
      if (!blob) {
        toast.show('Could not build the image', 'error');
        return;
      }
      const slug = view.playMode === 'daily' && view.day ? `${view.mode.id}-${view.day}` : `${view.mode.id}-endless`;
      const res = await shareImageOrDownload(blob, `cluevine-${slug}.png`, shareText());
      if (res === 'downloaded') toast.show('Image saved', 'success');
      else if (res === 'failed') toast.show('Could not share the image', 'error');
    } finally {
      setBusy(false);
    }
  };

  const onCopyText = async () => {
    const text = shareText();
    if (canNativeShare() && (await nativeShare(text))) return;
    const ok = await copyText(text);
    toast.show(ok ? 'Result copied to clipboard' : 'Could not copy', ok ? 'success' : 'error');
  };

  const onCopyLink = async () => {
    const ok = await copyText(link);
    toast.show(ok ? 'Challenge link copied — send it to a friend' : 'Could not copy', ok ? 'success' : 'error');
  };

  const beat = view.challenge?.beat;
  const rival = view.challenge?.by || 'your friend';

  return (
    <Modal open onClose={onClose} ariaLabel={`${view.mode.name} result`} dismissable>
      <div className="cv-result">
        <span
          className="cv-chip"
          style={{
            background: won ? 'var(--vine-wash)' : 'var(--berry-wash)',
            color: won ? 'var(--vine-deep)' : 'var(--berry)',
          }}
        >
          {view.mode.name} · {tag}
        </span>
        <h2 className="cv-result__verdict">{summary.headline}</h2>
        {reveal ? (
          <p className="cv-muted">
            {revealLabel} <span className="cv-result__answer">{reveal}</span>
          </p>
        ) : null}

        {grid.length ? (
          <div className="cv-result__grid" aria-hidden="true">
            {grid.map((row, i) => (
              <span key={i}>{row}</span>
            ))}
          </div>
        ) : null}

        <div className="cv-result__score">{commas(breakdown.total)}</div>

        {typeof beat === 'number' ? (
          <p className="cv-result__versus" data-win={won && breakdown.total > beat ? 'true' : undefined}>
            {won && breakdown.total > beat
              ? `You beat ${rival}’s ${commas(beat)}! 🌿`
              : won && breakdown.total === beat
                ? `Dead heat with ${rival} at ${commas(beat)}.`
                : `${rival} scored ${commas(beat)} — rematch?`}
          </p>
        ) : null}

        {view.eased ? (
          <p className="cv-faint" style={{ fontSize: 'var(--text-xs)' }}>
            Eased to {view.difficultyLabel} after a tough run
            {view.requestedLabel ? ` — you’re back on ${view.requestedLabel} after a win` : ''}.
          </p>
        ) : null}

        <ScoreBreakdown breakdown={breakdown} />

        {won && view.playMode === 'daily' && !view.archive && view.day === today ? (
          <PlantProgress streak={overall(stats, today).currentBestStreak} compact customize={false} />
        ) : null}

        {view.playMode === 'daily' && !view.archive ? <NextDaily /> : null}

        <div className="cv-result__actions">
          <Button variant="primary" block iconLeft="share" loading={busy} disabled={busy} onClick={onShareImage}>
            Share result
          </Button>
          <div className="cv-row" style={{ width: '100%', gap: 'var(--space-xs)' }}>
            {upNext && onUpNext ? (
              <Button variant="secondary" block iconLeft="play" onClick={onUpNext}>
                Next: {upNext.mode.name}
                <span className="cv-faint"> · {upNext.remaining} left</span>
              </Button>
            ) : onPlayAgain ? (
              <Button variant="secondary" block iconLeft="play" onClick={onPlayAgain}>
                {view.playMode === 'endless' ? 'Play again' : 'Play endless'}
              </Button>
            ) : null}
            <Button variant="ghost" block iconLeft="home" onClick={onHome}>
              Home
            </Button>
          </div>
          <div className="cv-row" style={{ gap: 'var(--space-md)', justifyContent: 'center' }}>
            <button type="button" className="cv-result__copylink" onClick={onCopyText}>
              Copy as text
            </button>
            {view.ref ? (
              <button type="button" className="cv-result__copylink" onClick={onCopyLink}>
                Challenge a friend
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </Modal>
  );
}
